import { Injectable } from '@angular/core';

import * as CryptoJS from 'crypto-js';

type WordArray = CryptoJS.lib.WordArray;

export enum Encoding {
  Utf8,
  Base64,
  Hex,
}

export interface EncodeOptions {
  /**
   * The message itself
   */
  message: string;
  /**
   * The source encoding
   */
  input: Encoding;
  /**
   * The target encoding
   */
  output: Encoding;
}

/**
 * @param encoding The encoding for which the encoder should be acquired
 *
 * @returns
 * The encoder for the encoding
 */
function getEncoder(encoding: Encoding) {
  switch (encoding) {
    case Encoding.Base64:
      return CryptoJS.enc.Base64;
    case Encoding.Utf8:
      return CryptoJS.enc.Utf8;
    case Encoding.Hex:
      return CryptoJS.enc.Hex;
    default:
      throw new Error('Encoder not found');
  }
}

@Injectable()
export class EncodingService {
  /**
   * @param message The message to encode
   * @param encoding The target encoding
   *
   * @returns
   * A binary representation of a message with a given encoding
   */
  asBinary(message: string, encoding: Encoding): WordArray {
    const encoder = getEncoder(encoding);
    return encoder.parse(message);
  }

  /**
   * @param options The encoding options
   *
   * @returns
   * The string representation of message with the given options.
   */
  encode(options: EncodeOptions): string {
    const { message, input, output } = options;
    const outputEncoder = getEncoder(output);
    const wordArray = this.asBinary(message, input);
    return wordArray.toString(outputEncoder);
  }
}
